import React, { useCallback, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import createGlobe from 'cobe';

// Configuration object for the globe's appearance and behavior
const GLOBE_CONFIG = {
  width: 800,
  height: 800,
  devicePixelRatio: 2,
  phi: 0,
  theta: 0.3,
  dark: 0,
  diffuse: 0.4,
  mapSamples: 16000,
  mapBrightness: 1.2,
  baseColor: [1, 1, 1],
  markerColor: [251/255, 100/255, 21/255], // Orange color for markers
  glowColor: [0.8, 0.8, 1], // Original blue tint for globe glow
  markers: [
    // Ho Chi Minh City - Central Vietnam
    { 
      id: 'SIMULACRA_01',
      location: [10.8231, 106.6297],
      size: 0.1,
      pulseScale: [0.1, 0.15], // Range for size pulsing
      pulseSpeed: 1.5          // Speed of pulse animation
    },
    // New York - Eastern United States
    {
      id: 'SIMULACRA_02',
      location: [40.7128, -73.9360],
      size: 0.1,
      pulseScale: [0.1, 0.15],
      pulseSpeed: 1.5
    },
    // Istanbul - Turkey
    {
      id: 'SIMULACRA_03',
      location: [41.0082, 29.0584],
      size: 0.1,
      pulseScale: [0.1, 0.15],
      pulseSpeed: 1.5
    }
  ],
  atmosphereColor: [0.5, 0.6, 1],
  atmosphereScale: 1.1,
  scale: 1,
  offset: [0, 0],
  opacity: 0.9,
};

// Color definitions for UI elements matching the globe markers
export const MARKER_COLORS = {
  'SIMULACRA_01': '#FB6415',
  'SIMULACRA_02': '#FB6415',
  'SIMULACRA_03': '#FB6415'
};

// Easing function for smooth animation transitions
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

// Utility function to calculate marker size for pulsing animation
const getMarkerSize = (marker, time) => {
  const [minSize, maxSize] = marker.pulseScale;
  const speed = marker.pulseSpeed;
  const scale = (Math.sin(time * speed) + 1) / 2;
  return minSize + (maxSize - minSize) * scale;
};

const Globe = forwardRef(({ onMarkerClick }, ref) => {
  const canvasRef = useRef(null);
  const rotationRef = useRef({ phi: 0, theta: 0.3 });
  const targetRotationRef = useRef({ phi: 0, theta: 0.3 });
  const autoRotationRef = useRef(0);
  const globeInstanceRef = useRef(null);
  const isRotatingRef = useRef(true);
  const animationStartTimeRef = useRef(null);
  const animationDurationRef = useRef(1000);
  const timeRef = useRef(0);

  useImperativeHandle(ref, () => ({
    rotateToMarker: (markerId) => {
      const marker = GLOBE_CONFIG.markers.find(m => m.id === markerId);
      if (marker) {
        isRotatingRef.current = false;
        
        let phi, theta;
        
        switch (markerId) {
          case 'SIMULACRA_01':
            const [lat1, long1] = marker.location;
            phi = -((long1 + 90) * Math.PI) / 180;
            theta = -((lat1 - 20) * Math.PI) / 180;
            break;
            
          case 'SIMULACRA_02':
            const [lat2, long2] = marker.location;
            phi = -((long2 + 90) * Math.PI) / 180;
            theta = -((lat2 - 80) * Math.PI) / 180;
            break;
            
          case 'SIMULACRA_03':
            const [lat3, long3] = marker.location;
            phi = -((long3 + 90) * Math.PI) / 180;
            theta = -((lat3 - 80) * Math.PI) / 180;
            break;
            
          default:
            const [lat, long] = marker.location;
            phi = -((long + 90) * Math.PI) / 180;
            theta = -((lat - 20) * Math.PI) / 180;
        }

        const startPhi = rotationRef.current.phi;
        const startTheta = rotationRef.current.theta;

        targetRotationRef.current = { phi, theta };
        
        animationStartTimeRef.current = Date.now();
        
        let phiDiff = targetRotationRef.current.phi - startPhi;
        if (Math.abs(phiDiff) > Math.PI) {
          if (phiDiff > 0) {
            phiDiff = phiDiff - 2 * Math.PI;
          } else {
            phiDiff = phiDiff + 2 * Math.PI;
          }
        }
        
        rotationRef.current.startValues = {
          phi: startPhi,
          theta: startTheta,
          phiDiff: phiDiff,
          thetaDiff: targetRotationRef.current.theta - startTheta
        };
      }
    },
    resumeRotation: () => {
      isRotatingRef.current = true;
      autoRotationRef.current = rotationRef.current.phi;
    }
  }));

  const onRender = useCallback((state) => {
    timeRef.current += 0.01;

    // Update marker sizes for pulsing effect
    state.markers = GLOBE_CONFIG.markers.map(marker => ({
      ...marker,
      size: getMarkerSize(marker, timeRef.current)
    }));

    if (isRotatingRef.current) {
      autoRotationRef.current += 0.003;
      state.phi = autoRotationRef.current;
      state.theta = rotationRef.current.theta;
    } else if (animationStartTimeRef.current) {
      const elapsed = Date.now() - animationStartTimeRef.current;
      const progress = Math.min(elapsed / animationDurationRef.current, 1);
      
      if (progress < 1) {
        const easedProgress = easeOutCubic(progress);
        
        state.phi = rotationRef.current.startValues.phi + 
                   rotationRef.current.startValues.phiDiff * easedProgress;
        state.theta = rotationRef.current.startValues.theta + 
                     rotationRef.current.startValues.thetaDiff * easedProgress;
        
        rotationRef.current.phi = state.phi;
        rotationRef.current.theta = state.theta;
      } else {
        state.phi = targetRotationRef.current.phi;
        state.theta = targetRotationRef.current.theta;
        rotationRef.current.phi = state.phi;
        rotationRef.current.theta = state.theta;
        animationStartTimeRef.current = null;
      }
    } else {
      state.phi = rotationRef.current.phi;
      state.theta = rotationRef.current.theta;
    }
  }, []);

  useEffect(() => {
    let width = 0;
    const onResize = () => {
      if (canvasRef.current) {
        width = canvasRef.current.offsetWidth;
      }
    };
    
    window.addEventListener('resize', onResize);
    onResize();

    const globe = createGlobe(canvasRef.current, {
      ...GLOBE_CONFIG,
      width: width * 2,
      height: width * 2,
      onRender,
    });

    globeInstanceRef.current = globe;

    if (canvasRef.current) {
      canvasRef.current.style.opacity = '1';
    }

    return () => {
      window.removeEventListener('resize', onResize);
      globe.destroy();
    };
  }, [onRender]);

  const handleClick = useCallback((e) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (rect) {
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;

      if (x > 0.4 && x < 0.6 && y > 0.4 && y < 0.6) {
        onMarkerClick?.();
      }
    }
  }, [onMarkerClick]);

  return (
    <div className="absolute inset-0">
      <canvas
        ref={canvasRef}
        className="w-full h-full opacity-0 transition-opacity duration-500"
        style={{ 
          contain: 'layout paint size',
          filter: 'drop-shadow(0 0 10px rgba(120, 160, 255, 0.3))'
        }}
        onClick={handleClick}
      />
    </div>
  );
});

export default Globe;